import { Navigate, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuthStore } from '../../stores/auth'
import { useSupportStore } from '../../stores/support'
import { Upload, X } from 'lucide-react'

const TICKET_CATEGORIES = [
  { value: 'order', label: 'Order Issues' },
  { value: 'payment', label: 'Payment & Billing' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'product', label: 'Product Quality' },
  { value: 'account', label: 'Account & Login' },
  { value: 'other', label: 'Other' },
] as const

export default function NewTicketPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const { createTicket } = useSupportStore()

  const [subject, setSubject] = useState('')
  const [category, setCategory] = useState<'order' | 'payment' | 'delivery' | 'product' | 'account' | 'other'>('order')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [attachments, setAttachments] = useState<string[]>([])

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const ticketId = createTicket({
      user_id: user.id,
      subject,
      category,
      description,
      priority,
      attachments,
    })

    alert(`✓ Support ticket created successfully!\n\nTicket ID: ${ticketId}\n\nOur team will respond within 24 hours.`)
    navigate('/support')
  }

  const handleAddAttachment = () => {
    // In a real app, this would handle file upload
    const mockUrl = `https://source.unsplash.com/400x400/?document&sig=${Date.now()}`
    setAttachments([...attachments, mockUrl])
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Submit a Support Ticket</h1>
      <p className="text-gray-600 mb-8">
        Describe your issue in detail and our support team will get back to you within 24 hours.
      </p>

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        {/* Subject */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Subject *
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of your issue"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Category *
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as typeof category)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TICKET_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Priority
          </label>
          <div className="flex gap-4">
            {(['low', 'medium', 'high'] as const).map((p) => (
              <label key={p} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value={p}
                  checked={priority === p}
                  onChange={(e) => setPriority(e.target.value as typeof priority)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="capitalize">{p}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Description *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe your issue in detail..."
            required
            rows={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Attachments (Optional)
          </label>
          <div className="space-y-3">
            {attachments.map((url, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img src={url} alt="" className="w-12 h-12 object-cover rounded" />
                <span className="flex-1 text-sm text-gray-600">Attachment {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            {attachments.length < 3 && (
              <button
                type="button"
                onClick={handleAddAttachment}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Add Attachment</span>
              </button>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/support')}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Submit Ticket
          </button>
        </div>
      </form>
    </div>
  )
}
